import {
  useInfiniteQuery,
  useQuery,
  type DefaultError,
  type InfiniteData,
  type QueryKey,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';

import { useUserScope } from './useUserScope';

import type { UserScopedFactory } from '@/util/userScope';

/** What a caller may add to a feature's options. The key and the fetch are the factory's. */
export type UserScopedOverrides<TOptions> = Omit<Partial<TOptions>, 'queryKey' | 'queryFn'>;

type Enabled = boolean | ((query: never) => boolean) | undefined;

type Placeholder =
  | ((previous: unknown, previousQuery?: { queryKey: QueryKey }) => unknown)
  | unknown;

type ScopedOptions = {
  queryKey: QueryKey;
  enabled?: unknown;
  placeholderData?: unknown;
  meta?: Record<string, unknown>;
};

/** Names a key by its contents, so the key a factory builds matches the query that holds it. */
const keyIdentity = (queryKey: QueryKey) => JSON.stringify(queryKey);

/** Both conditions must hold. A function form is resolved against the query, as TanStack does. */
function bothEnabled(first: Enabled, second: Enabled): Enabled {
  if (first === undefined) {
    return second;
  }

  if (second === undefined) {
    return first;
  }

  if (first === false || second === false) {
    return false;
  }

  return (query: never) =>
    (typeof first === 'function' ? first(query) : first) &&
    (typeof second === 'function' ? second(query) : second);
}

/**
 * Builds the factory's options for the account in use and applies every rule
 * about accounts, so no call site has to:
 *
 * - a `user` query is held until there is a user who can fetch;
 * - the caller's `enabled` and `meta` join the factory's rather than replacing them;
 * - a placeholder never shows another account's data. The hook records which
 *   account each key it built belongs to, and a placeholder function only sees
 *   previous data that came from a key built for the same account, so
 *   `keepPreviousData` cannot carry one account's list into the next.
 */
function useScopedOptions<TArgs extends unknown[], TOptions extends ScopedOptions>(
  factory: UserScopedFactory<TArgs, TOptions>,
  args: TArgs,
  overrides: Partial<TOptions>
): { options: TOptions; held: boolean } {
  if (factory.userScope !== 'user' && factory.userScope !== 'viewer') {
    throw new Error('useUserScopedQuery needs a factory declared with userScoped().');
  }

  const { userId, canFetch } = useUserScope();

  const held = factory.userScope === 'user' && !canFetch;

  const base = factory(userId, ...args);

  const accounts = useRef(new Map<string, string | undefined>());

  accounts.current.set(keyIdentity(base.queryKey), userId);

  const placeholderData = (
    overrides.placeholderData !== undefined ? overrides.placeholderData : base.placeholderData
  ) as Placeholder;

  const scopedPlaceholder = useMemo(() => {
    if (typeof placeholderData !== 'function') {
      return placeholderData;
    }

    return (previous: unknown, previousQuery?: { queryKey: QueryKey }) => {
      const identity = previousQuery && keyIdentity(previousQuery.queryKey);

      const sameAccount =
        identity !== undefined &&
        accounts.current.has(identity) &&
        accounts.current.get(identity) === userId;

      return sameAccount
        ? placeholderData(previous, previousQuery)
        : placeholderData(undefined, undefined);
    };
  }, [placeholderData, userId]);

  const meta = base.meta || overrides.meta ? { ...base.meta, ...overrides.meta } : undefined;

  return {
    held,
    options: {
      ...base,
      ...overrides,
      enabled: bothEnabled(
        bothEnabled(base.enabled as Enabled, overrides.enabled as Enabled),
        held ? false : undefined
      ),
      meta,
      placeholderData: scopedPlaceholder,
    },
  };
}

type FetchingResult = { isPending: boolean };

/**
 * While a query is held for want of an account, nothing can fetch it and its
 * answer is not known yet. TanStack's `refetch` and page fetches ignore
 * `enabled`, and a held query reads as not loading, so both are corrected
 * here: fetches resolve without a request, and `isLoading` reads as pending.
 * Callers keep using `isLoading` and `refetch` as they would anywhere else.
 */
function heldResult<TResult extends FetchingResult>(result: TResult, held: boolean): TResult {
  if (!held) {
    return result;
  }

  const inert = () => Promise.resolve(result);

  return new Proxy(result, {
    get(target, property, receiver) {
      if (
        property === 'refetch' ||
        property === 'fetchNextPage' ||
        property === 'fetchPreviousPage'
      ) {
        return inert;
      }

      if (property === 'isLoading') {
        return target.isPending;
      }

      return Reflect.get(target, property, receiver);
    },
  });
}

/**
 * A query whose data belongs to an account, from a factory declared with
 * `userScoped`. Pass the factory's arguments after the account id, and any
 * extra options, such as `enabled`, as `overrides`.
 */
export function useUserScopedQuery<
  TArgs extends unknown[],
  TQueryFnData,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  factory: UserScopedFactory<TArgs, UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>>,
  args: TArgs,
  overrides: UserScopedOverrides<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>> = {}
): UseQueryResult<TData, TError> {
  const { options, held } = useScopedOptions(factory, args, overrides);

  return heldResult(useQuery(options), held);
}

/** `useUserScopedQuery` for a paged list. */
export function useUserScopedInfiniteQuery<
  TArgs extends unknown[],
  TQueryFnData,
  TError = DefaultError,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  factory: UserScopedFactory<
    TArgs,
    UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>
  >,
  args: TArgs,
  overrides: UserScopedOverrides<
    UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>
  > = {}
): UseInfiniteQueryResult<TData, TError> {
  const { options, held } = useScopedOptions(factory, args, overrides);

  return heldResult(useInfiniteQuery(options), held);
}

/**
 * The account's key for a `userScoped` factory, for code that reads or writes
 * the cache directly: optimistic updates, rollbacks, invalidations and cache
 * reads. It is the same key the query hooks use, so the two cannot drift.
 */
export function useUserScopedKey<TArgs extends unknown[], TQueryKey extends QueryKey>(
  factory: UserScopedFactory<TArgs, { queryKey: TQueryKey }>
) {
  const { userId } = useUserScope();

  return useCallback((...args: TArgs) => factory(userId, ...args).queryKey, [factory, userId]);
}
